import { ApiClientBase } from '@/common/services/api/ApiClientBase';
import type { ApiResponse, RequestConfig } from '@/common/services/api/types';
import type { AcsToken } from '../types';

/** Marge avant expiration réelle pour déclencher un refresh proactif (5 min). */
const TOKEN_REFRESH_MARGIN_MS = 5 * 60 * 1000;

export type RefreshTokenFn = () => Promise<AcsToken>;

/**
 * Client API dédié au chat.
 * Étend ApiClientBase (même base URL que le BFF) et injecte automatiquement
 * le `acsToken` en query parameter sur chaque requête.
 *
 * Gère le cycle de vie du token ACS :
 * - Refresh proactif si le token est sur le point d'expirer.
 * - Retry automatique sur 401 (token expiré côté serveur).
 */
export class ChatApiClient extends ApiClientBase {
  private static instance: ChatApiClient;
  private acsToken: string | null = null;
  private expiresOn: Date | null = null;
  private refreshTokenFn: RefreshTokenFn | null = null;
  private refreshPromise: Promise<AcsToken> | null = null;

  private constructor() {
    super(import.meta.env.VITE_API_BASE_URL);
  }

  static getInstance(): ChatApiClient {
    if (!this.instance) {
      this.instance = new ChatApiClient();
    }
    return this.instance;
  }

  setAcsToken(acsToken: AcsToken): void {
    this.acsToken = acsToken.token;
    this.expiresOn = new Date(acsToken.expiresOn);
  }

  setRefreshTokenFn(fn: RefreshTokenFn): void {
    this.refreshTokenFn = fn;
  }

  getAcsToken(): string | null {
    return this.acsToken;
  }

  clearAcsToken(): void {
    this.acsToken = null;
    this.expiresOn = null;
    this.refreshTokenFn = null;
    this.refreshPromise = null;
  }

  private isTokenExpiringSoon(): boolean {
    if (!this.expiresOn) return true;
    return Date.now() + TOKEN_REFRESH_MARGIN_MS >= this.expiresOn.getTime();
  }

  /**
   * Rafraîchit le token ACS. Déduplique les appels concurrents
   * (un seul refresh en vol à la fois).
   */
  private async refreshToken(): Promise<void> {
    if (!this.refreshTokenFn) {
      throw new Error(
        '[ChatApiClient] No refresh function configured. Call setRefreshTokenFn() first.'
      );
    }

    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshTokenFn();
    }

    try {
      const newToken = await this.refreshPromise;
      this.setAcsToken(newToken);
    } finally {
      this.refreshPromise = null;
    }
  }

  protected async request<T = unknown>(
    endpoint: string,
    config: RequestConfig
  ): Promise<ApiResponse<T>> {
    if (!this.acsToken) {
      throw new Error('[ChatApiClient] ACS token not set. Call setAcsToken() first.');
    }

    // Refresh proactif avant l'appel si le token expire bientôt
    if (this.isTokenExpiringSoon() && this.refreshTokenFn) {
      await this.refreshToken();
    }

    const configWithAcsToken: RequestConfig = {
      ...config,
      headers: {
        ...config.headers,
        'X-User-ACS-Token': this.acsToken,
      },
    };

    try {
      return await super.request<T>(endpoint, configWithAcsToken);
    } catch (error: any) {
      // Retry une seule fois sur 401 (token expiré côté serveur)
      if (error?.status === 401 && this.refreshTokenFn) {
        await this.refreshToken();
        configWithAcsToken.headers = {
          ...config.headers,
          'X-User-ACS-Token': this.acsToken!,
        };
        return super.request<T>(endpoint, configWithAcsToken);
      }
      throw error;
    }
  }
}
